import React, { useState, useEffect, useCallback } from "react";
import Header from "../../../commons/Header/Header";
import Sidebar from "../../../commons/Sidebar/Sidebar";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { handleApiCall } from "../../../apis";
import { endpoints } from "../../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../../utils/constants";

const formatSubscriptionDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatBillingCycle = (subscription) => {
  const cycle = subscription?.subscription_plan_id?.billing_cycle || subscription?.billing_cycle;
  if (!cycle) return "N/A";

  if (cycle === "one_time") {
    const months = subscription?.subscription_plan_id?.plan_duration_months;
    return months ? `${months} Month${months > 1 ? "s" : ""}` : "One Time";
  }

  return String(cycle)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatSubscriptionAmount = (subscription) => {
  const amount = subscription?.amount ?? subscription?.payment ?? subscription?.price;
  if (amount === undefined || amount === null || amount === "") return "N/A";

  const currency = String(subscription?.currency || "gbp").toUpperCase();
  const symbol = currency === "GBP" ? "£" : currency === "USD" ? "$" : currency === "EUR" ? "€" : "";
  return `${symbol}${amount}`;
};

const getSubscriptionStatusLabel = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  if (status === 1 || normalized === "active") return "Active";
  if (status === 0 || normalized === "inactive") return "Inactive";
  if (normalized === "expired") return "Expired";
  if (normalized === "pending") return "Pending";
  return status ?? "N/A";
};

const extractSubscriptionDetail = (responseData) => {
  if (responseData?.subscription && typeof responseData.subscription === "object") {
    return responseData.subscription;
  }
  if (responseData && typeof responseData === "object" && !Array.isArray(responseData)) {
    return responseData;
  }
  return null;
};

function SubscriptionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const getRestaurantViewPath = () => {
    if (location.state?.from) return location.state.from;

    const restaurantViewId =
      searchParams.get("restaurantViewId") || sessionStorage.getItem("restaurantViewId");
    if (restaurantViewId) {
      return `/menu-and-restaurant/view-restaurant/${restaurantViewId}`;
    }

    return null;
  };

  const handleBack = () => {
    const backPath = getRestaurantViewPath();
    if (backPath) {
      navigate(backPath);
      return;
    }
    navigate("/menu-and-restaurant-management");
  };

  const getSubscriptionDetails = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await handleApiCall(
        "get",
        `${endpoints.restaurantSubscriptionDetail}/${id}`
      );

      if (response?.error) return;

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setSubscription(extractSubscriptionDetail(response?.data?.data));
      } else if (response?.data?.message) {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching subscription details:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getSubscriptionDetails();
  }, [getSubscriptionDetails]);

  if (loading) {
    return (
      <div className="container-scroller">
        <Header />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper">
              <div className="heading mb-3 d-flex align-items-center">
                <h3 className="text-default weight-400 my-0">View Subscription Details</h3>
              </div>
              <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">View Subscription Details</h3>
            </div>
            <div className="d-flex">
              <div className="mt-3">
                <button type="button" className="btn btn-default rounded-lg" onClick={handleBack}>
                  Back
                </button>
              </div>
            </div>
            {subscription ? (
              <div className="rounded-14 border px-3 py-2 mt-4">
                <div className="row mx-n2">
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Subscription ID</strong>
                      <p className="mb-0 mt-2">#{subscription._id?.slice(-8) || "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Subscription</strong>
                      <p className="mb-0 mt-2">
                        {subscription.plan_name || subscription.subscription_plan_id?.name || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Subscription Plan</strong>
                      <p className="mb-0 mt-2">{formatBillingCycle(subscription)}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Start Date</strong>
                      <p className="mb-0 mt-2">{formatSubscriptionDate(subscription.start_date)}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Renewal Date</strong>
                      <p className="mb-0 mt-2">{formatSubscriptionDate(subscription.expiry_date)}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Status</strong>
                      <p className="mb-0 mt-2">
                        {getSubscriptionStatusLabel(subscription.subscription_status || subscription.status)}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Payment</strong>
                      <p className="mb-0 mt-2">{formatSubscriptionAmount(subscription)}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Call Limit</strong>
                      <p className="mb-0 mt-2">{subscription.purchased_call_limit ?? "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Remaining Calls</strong>
                      <p className="mb-0 mt-2">{subscription.remaining_call_limit ?? "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Used Calls</strong>
                      <p className="mb-0 mt-2">{subscription.used_call_limit ?? "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Payment Method</strong>
                      <p className="mb-0 mt-2 text-capitalize">{subscription.payment_method || "N/A"}</p>
                    </div>
                  </div>
                  <div className="col-md-4 p-2">
                    <div className="rounded-14 border p-3">
                      <strong>Paid At</strong>
                      <p className="mb-0 mt-2">{formatSubscriptionDate(subscription.paid_at)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 mt-4">
                <p>No subscription details available</p>
                <Link to="/menu-and-restaurant-management" className="btn btn-default rounded-lg mt-2">
                  Back to list
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionDetails;
